
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  Button,
  TextField,
  Typography,
  Slider,
  IconButton,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Alert,
} from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  Upload,
  Add,
  Delete,
  Download,
  TextFields,
  Link as LinkIcon,
  Category,
  Palette,
  CloudUpload,
} from "@mui/icons-material";
import * as fabric from "fabric";
import axios from "axios";

export default function ImageEditor() {
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Canvas dimensions - 600x700
  const CANVAS_WIDTH = 550;
  const CANVAS_HEIGHT = 730;
  
  // QUALITY MULTIPLIER - Increase this for even better quality (3x or 4x)
  const EXPORT_QUALITY_MULTIPLIER = 3; // This will export at 1800x2100px

  // Canvas background color
  const [canvasBackgroundColor, setCanvasBackgroundColor] = useState("#f0f0f0");

  // Text properties
  const [currentText, setCurrentText] = useState("");
  const [fontSize, setFontSize] = useState(24);
  const [textColor, setTextColor] = useState("#FFFFFF");
  const [bold, setBold] = useState(false);
  const [italic, setItalic] = useState(false);
  const [underline, setUnderline] = useState(false);
  const [textAlign, setTextAlign] = useState("left");

  // Link properties
  const [isLinkEnabled, setIsLinkEnabled] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");

  // Background properties (WhatsApp style)
  const [hasBackground, setHasBackground] = useState(true);
  const [backgroundColor, setBackgroundColor] = useState("#000000");
  const [backgroundOpacity, setBackgroundOpacity] = useState(0.6);

  // Category checkboxes
  const [categories, setCategories] = useState({
    student: false,
    institute: false,
    staff: false,
    login: false,
  });

  const [selectedObject, setSelectedObject] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // API upload states
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploadError, setUploadError] = useState(false);

  useEffect(() => {
    // Initialize Fabric.js canvas with MAXIMUM QUALITY settings
    const canvas = new fabric.Canvas(canvasRef.current, {
      width: CANVAS_WIDTH,
      height: CANVAS_HEIGHT,
      backgroundColor: canvasBackgroundColor,
      enableRetinaScaling: true, // Enable retina/high-DPI scaling
      imageSmoothingEnabled: true, // Smooth image rendering
      renderOnAddRemove: true, // Render immediately for quality
      skipOffscreen: false, // Don't skip rendering for quality
    });

    fabricCanvasRef.current = canvas;

    // Handle object selection
    canvas.on("selection:created", (e) => {
      setSelectedObject(e.selected[0]);
      updateControlsFromObject(e.selected[0]);
    });

    canvas.on("selection:updated", (e) => {
      setSelectedObject(e.selected[0]);
      updateControlsFromObject(e.selected[0]);
    });

    canvas.on("selection:cleared", () => {
      setSelectedObject(null);
      resetControls();
    });

    // Listen for text changes when editing directly on canvas
    canvas.on("text:changed", (e) => {
      if (e.target && e.target.type === "textbox") {
        setCurrentText(e.target.text);
      }
    });

    canvas.on("text:editing:entered", (e) => {
      if (e.target && e.target.type === "textbox") {
        setCurrentText(e.target.text);
      }
    });

    canvas.on("text:editing:exited", (e) => {
      if (e.target && e.target.type === "textbox") {
        setCurrentText(e.target.text);
      }
    });

    // Handle mouse clicks for links (in editor mode)
    canvas.on("mouse:down", (options) => {
      if (options.target && options.target.linkUrl) {
        const pointer = canvas.getPointer(options.e);
        if (options.target.containsPoint(pointer)) {
          window.open(options.target.linkUrl, "_blank");
        }
      }
    });

    // Change cursor when hovering over linked text
    canvas.on("mouse:over", (options) => {
      if (options.target && options.target.linkUrl) {
        canvas.defaultCursor = "pointer";
        canvas.renderAll();
      }
    });

    canvas.on("mouse:out", (options) => {
      if (options.target && options.target.linkUrl) {
        canvas.defaultCursor = "default";
        canvas.renderAll();
      }
    });

    return () => {
      canvas.dispose();
    };
  }, []);

  const updateControlsFromObject = (obj) => {
    if (obj && obj.type === "textbox") {
      setCurrentText(obj.text);
      setFontSize(obj.fontSize);
      setTextColor(obj.fill);
      setBold(obj.fontWeight === "bold");
      setItalic(obj.fontStyle === "italic");
      setUnderline(obj.underline);
      setTextAlign(obj.textAlign);

      if (obj.linkUrl) {
        setIsLinkEnabled(true);
        setLinkUrl(obj.linkUrl);
      } else {
        setIsLinkEnabled(false);
        setLinkUrl("");
      }

      if (obj.categories) {
        setCategories(obj.categories);
      } else {
        setCategories({
          student: false,
          institute: false,
          staff: false,
          login: false,
        });
      }

      if (obj.backgroundColor && obj.backgroundColor !== "transparent") {
        setHasBackground(true);
        setBackgroundOpacity(obj.backgroundOpacity || 0.6);
      } else {
        setHasBackground(false);
      }
    }
  };

  const resetControls = () => {
    setCurrentText("");
    setIsLinkEnabled(false);
    setLinkUrl("");
  };

  const handleCategoryChange = (event) => {
    setCategories({
      ...categories,
      [event.target.name]: event.target.checked,
    });
  };

  const handleCanvasBackgroundChange = (color) => {
    setCanvasBackgroundColor(color);
    if (fabricCanvasRef.current) {
      fabricCanvasRef.current.backgroundColor = color;
      fabricCanvasRef.current.renderAll();
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file && !isLoading) {
      setIsLoading(true);
      const reader = new FileReader();
      reader.onload = async (e) => {
        const canvas = fabricCanvasRef.current;

        const objects = canvas.getObjects();
        objects.forEach((obj) => {
          if (obj.isBackgroundImage) {
            canvas.remove(obj);
          }
        });

        try {
          const img = await fabric.FabricImage.fromURL(e.target.result, {
            crossOrigin: "anonymous",
          });

          const canvasAspect = CANVAS_WIDTH / CANVAS_HEIGHT;
          const imgAspect = img.width / img.height;

          let scale;
          if (imgAspect > canvasAspect) {
            scale = CANVAS_WIDTH / img.width;
          } else {
            scale = CANVAS_HEIGHT / img.height;
          }

          img.scale(scale);

          img.set({
            left: (CANVAS_WIDTH - img.width * scale) / 2,
            top: (CANVAS_HEIGHT - img.height * scale) / 2,
            selectable: false,
            evented: false,
            isBackgroundImage: true,
          });

          canvas.add(img);
          canvas.sendToBack(img);
          canvas.renderAll();
          setIsLoading(false);
        } catch (error) {
          console.error("Error loading image:", error);
          setIsLoading(false);
        }
      };
      reader.readAsDataURL(file);
    }
    event.target.value = "";
  };

  const addTextToCanvas = () => {
    if (currentText.trim() === "") return;

    const canvas = fabricCanvasRef.current;

    console.log("Adding text with link:", {
      text: currentText,
      isLinkEnabled: isLinkEnabled,
      linkUrl: linkUrl,
    });

    const textbox = new fabric.Textbox(currentText, {
      left: 100,
      top: 100,
      fontSize: fontSize,
      fill: textColor,
      fontWeight: bold ? "bold" : "normal",
      fontStyle: italic ? "italic" : "normal",
      underline: underline || isLinkEnabled,
      textAlign: textAlign,
      backgroundColor: hasBackground
        ? hexToRgba(backgroundColor, backgroundOpacity)
        : "transparent",
      padding: hasBackground ? 8 : 0,
      width: 400,
      editable: true,
      splitByGrapheme: true,
      backgroundOpacity: backgroundOpacity,
      linkUrl: isLinkEnabled && linkUrl ? linkUrl : null,
      hoverCursor: isLinkEnabled && linkUrl ? "pointer" : "move",
      categories: { ...categories },
      // MAXIMUM TEXT QUALITY SETTINGS
      objectCaching: false, // Disable caching for sharpest text
      statefullCache: false, // No cache for quality
      noScaleCache: true, // Don't cache scaled versions
      strokeUniform: true, // Uniform strokes
      paintFirst: "fill", // Fill first for clarity
    });

    console.log("Created textbox with linkUrl:", textbox.linkUrl);

    canvas.add(textbox);
    canvas.setActiveObject(textbox);
    canvas.renderAll();

    // Only reset text and link inputs, NOT categories
    setCurrentText("");
    setLinkUrl("");
    setIsLinkEnabled(false);
  };

  const deleteSelected = () => {
    const canvas = fabricCanvasRef.current;
    const activeObject = canvas.getActiveObject();

    if (activeObject && !activeObject.isBackgroundImage) {
      canvas.remove(activeObject);
      canvas.renderAll();
      setSelectedObject(null);
    }
  };

  const downloadImage = () => {
    const canvas = fabricCanvasRef.current;
    
    // ULTRA HIGH QUALITY EXPORT with 3x resolution
    const dataURL = canvas.toDataURL({
      format: "png",
      quality: 1, // Maximum quality
      multiplier: EXPORT_QUALITY_MULTIPLIER, // 3x = 1800x2100px output
      enableRetinaScaling: true,
    });

    const link = document.createElement("a");
    link.download = `poster-${CANVAS_WIDTH}x${CANVAS_HEIGHT}-HQ.png`;
    link.href = dataURL;
    link.click();
  };

  const uploadToAPI = async () => {
    const selectedCategories = Object.keys(categories).filter(
      (key) => categories[key]
    );

    if (selectedCategories.length === 0) {
      setUploadStatus("Please select at least one category");
      setUploadError(true);
      setTimeout(() => {
        setUploadStatus("");
        setUploadError(false);
      }, 3000);
      return;
    }

    setUploading(true);
    setUploadError(false);
    setUploadStatus("Converting canvas to high-quality image...");

    try {
      const canvas = fabricCanvasRef.current;

      console.log("=== DEBUG: Starting link extraction ===");
      console.log("Total objects on canvas:", canvas.getObjects().length);

      // Extract all text objects with links (scaling coordinates by multiplier)
      const linkData = [];
      canvas.getObjects().forEach((obj, index) => {
        console.log(`Object ${index}:`, {
          type: obj.type,
          text: obj.text,
          linkUrl: obj.linkUrl,
          hasLink: !!obj.linkUrl,
        });

        if (obj.type === "textbox" && obj.linkUrl) {
          const bounds = obj.getBoundingRect();
          console.log("Found text with link! Bounds:", bounds);

          // Scale coordinates by export multiplier for accurate clickable areas
          linkData.push({
            url: obj.linkUrl,
            x: Math.round(bounds.left * EXPORT_QUALITY_MULTIPLIER),
            y: Math.round(bounds.top * EXPORT_QUALITY_MULTIPLIER),
            width: Math.round(bounds.width * EXPORT_QUALITY_MULTIPLIER),
            height: Math.round(bounds.height * EXPORT_QUALITY_MULTIPLIER),
            text: obj.text,
          });
        }
      });

      console.log("=== Final linkData to upload ===");
      console.log("Link count:", linkData.length);
      console.log("Link data:", JSON.stringify(linkData, null, 2));

      setUploadStatus("Uploading high-quality image to server...");

      // ULTRA HIGH QUALITY EXPORT: Convert canvas to Blob with 3x multiplier
      const blob = await new Promise((resolve) => {
        canvas.toCanvasElement(EXPORT_QUALITY_MULTIPLIER).toBlob(
          (blob) => {
            resolve(blob);
          },
          "image/png",
          1.0 // Maximum PNG quality
        );
      });

      console.log(`Image exported at ${CANVAS_WIDTH * EXPORT_QUALITY_MULTIPLIER}x${CANVAS_HEIGHT * EXPORT_QUALITY_MULTIPLIER}px`);

      // Create FormData
      const formData = new FormData();
      formData.append("usertype", JSON.stringify(selectedCategories));
      formData.append("attachment", blob, "poster.png");
      formData.append("description", JSON.stringify(linkData));

      console.log("FormData description:", JSON.stringify(linkData));

      // API call
      const token = localStorage.getItem("accessToken");
      const response = await axios.post(
        "https://erpapi.eduskillsfoundation.org/documents/addPosters2",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("Upload successful:", response.data);

      if (linkData.length > 0) {
        setUploadStatus(`✓ Upload successful! High-quality image with ${linkData.length} clickable link(s)`);
      } else {
        setUploadStatus("✓ Upload successful! High-quality image uploaded");
      }

      setUploadError(false);

      setTimeout(() => {
        setUploadStatus("");
      }, 4000);
    } catch (error) {
      console.error("Upload failed:", error);
      setUploadError(true);
      setUploadStatus(
        `Upload failed: ${error.response?.data?.message || error.message}`
      );
    } finally {
      setUploading(false);
    }
  };

  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const getSelectedCategories = () => {
    const selected = [];
    if (categories.student) selected.push("Student");
    if (categories.institute) selected.push("Institute");
    if (categories.staff) selected.push("Staff");
    if (categories.login) selected.push("Login");
    return selected.length > 0 ? selected.join(", ") : "None";
  };

  // Update selected object properties
  useEffect(() => {
    if (selectedObject && selectedObject.type === "textbox") {
      const updates = {
        fontSize: fontSize,
        fill: textColor,
        fontWeight: bold ? "bold" : "normal",
        fontStyle: italic ? "italic" : "normal",
        underline: underline || isLinkEnabled,
        textAlign: textAlign,
        backgroundColor: hasBackground
          ? hexToRgba(backgroundColor, backgroundOpacity)
          : "transparent",
        padding: hasBackground ? 8 : 0,
        backgroundOpacity: backgroundOpacity,
        linkUrl: isLinkEnabled && linkUrl ? linkUrl : selectedObject.linkUrl,
        hoverCursor: (isLinkEnabled && linkUrl) || selectedObject.linkUrl ? "pointer" : "move",
        categories: { ...categories },
        objectCaching: false, // Keep high quality
        statefullCache: false,
        noScaleCache: true,
      };

      if (
        currentText &&
        currentText !== selectedObject.text &&
        !selectedObject.isEditing
      ) {
        updates.text = currentText;
      }

      selectedObject.set(updates);
      fabricCanvasRef.current.renderAll();
    }
  }, [
    currentText,
    fontSize,
    textColor,
    bold,
    italic,
    underline,
    textAlign,
    hasBackground,
    backgroundColor,
    backgroundOpacity,
    isLinkEnabled,
    linkUrl,
    categories,
    selectedObject,
  ]);

  return (
    <Box sx={{ display: "flex", height: "100vh", bgcolor: "#f5f5f5" }}>
      {/* Left Panel - Editor Controls */}
      <Paper
        elevation={3}
        sx={{
          width: 380,
          p: 3,
          overflowY: "auto",
          borderRadius: 0,
        }}
      >
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Image Editor
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {/* Quality Info Banner */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="caption">
            <strong>Ultra High Quality Mode</strong>
            <br />
            Export Resolution: {CANVAS_WIDTH * EXPORT_QUALITY_MULTIPLIER}×{CANVAS_HEIGHT * EXPORT_QUALITY_MULTIPLIER}px ({EXPORT_QUALITY_MULTIPLIER}x)
          </Typography>
        </Alert>

        {/* Canvas Background Color */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            <Palette sx={{ mr: 1, verticalAlign: "middle" }} />
            Canvas Background
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" gutterBottom display="block">
              Background Color
            </Typography>
            <input
              type="color"
              value={canvasBackgroundColor}
              onChange={(e) => handleCanvasBackgroundChange(e.target.value)}
              style={{
                width: "100%",
                height: "45px",
                border: "2px solid #ddd",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            />
          </Box>

          <Typography variant="caption" display="block" gutterBottom>
            Quick Colors
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {[
              { color: "#ffffff", name: "White" },
              { color: "#f0f0f0", name: "Light Gray" },
              { color: "#e0e0e0", name: "Gray" },
              { color: "#000000", name: "Black" },
              { color: "#e3f2fd", name: "Light Blue" },
              { color: "#f3e5f5", name: "Light Purple" },
              { color: "#e8f5e9", name: "Light Green" },
              { color: "#fff3e0", name: "Light Orange" },
            ].map(({ color, name }) => (
              <Box
                key={color}
                onClick={() => handleCanvasBackgroundChange(color)}
                title={name}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: color,
                  border:
                    canvasBackgroundColor === color
                      ? "3px solid #1976d2"
                      : "2px solid #ddd",
                  borderRadius: 1,
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    transform: "scale(1.1)",
                    boxShadow: 2,
                  },
                }}
              />
            ))}
          </Stack>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Image Upload Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Upload Image
          </Typography>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleImageUpload}
            style={{ display: "none" }}
          />
          <Button
            variant="contained"
            startIcon={<Upload />}
            fullWidth
            onClick={() => fileInputRef.current.click()}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "Upload Image"}
          </Button>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            Image will auto-fit to {CANVAS_WIDTH}×{CANVAS_HEIGHT}px canvas (ultra high quality)
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Text Controls */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          <TextFields sx={{ mr: 1, verticalAlign: "middle" }} />
          Add/Edit Text
        </Typography>

        <TextField
          label="Text Content"
          fullWidth
          value={currentText}
          onChange={(e) => setCurrentText(e.target.value)}
          sx={{ mb: 2 }}
          multiline
          rows={2}
          helperText={
            selectedObject ? "Editing selected text" : "Enter text to add"
          }
        />

        {/* Text Formatting Buttons */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" display="block" gutterBottom>
            Text Style
          </Typography>
          <Stack direction="row" spacing={1}>
            <IconButton
              onClick={() => setBold(!bold)}
              color={bold ? "primary" : "default"}
              sx={{
                border: "1px solid #ddd",
                bgcolor: bold ? "#e3f2fd" : "transparent",
              }}
            >
              <FormatBold />
            </IconButton>
            <IconButton
              onClick={() => setItalic(!italic)}
              color={italic ? "primary" : "default"}
              sx={{
                border: "1px solid #ddd",
                bgcolor: italic ? "#e3f2fd" : "transparent",
              }}
            >
              <FormatItalic />
            </IconButton>
            <IconButton
              onClick={() => setUnderline(!underline)}
              color={underline ? "primary" : "default"}
              sx={{
                border: "1px solid #ddd",
                bgcolor: underline ? "#e3f2fd" : "transparent",
              }}
            >
              <FormatUnderlined />
            </IconButton>
          </Stack>
        </Box>

        {/* Text Alignment */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Text Align</InputLabel>
          <Select
            value={textAlign}
            label="Text Align"
            onChange={(e) => setTextAlign(e.target.value)}
          >
            <MenuItem value="left">Left</MenuItem>
            <MenuItem value="center">Center</MenuItem>
            <MenuItem value="right">Right</MenuItem>
            <MenuItem value="justify">Justify</MenuItem>
          </Select>
        </FormControl>

        {/* Font Size */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" gutterBottom>
            Font Size: {fontSize}px
          </Typography>
          <Slider
            value={fontSize}
            onChange={(e, value) => setFontSize(value)}
            min={12}
            max={120}
            valueLabelDisplay="auto"
          />
        </Box>

        {/* Text Color */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" gutterBottom display="block">
            Text Color
          </Typography>
          <input
            type="color"
            value={textColor}
            onChange={(e) => setTextColor(e.target.value)}
            style={{
              width: "100%",
              height: "45px",
              border: "2px solid #ddd",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Hyperlink Section */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          <LinkIcon sx={{ mr: 1, verticalAlign: "middle" }} />
          Add Hyperlink
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={isLinkEnabled}
              onChange={(e) => setIsLinkEnabled(e.target.checked)}
            />
          }
          label="Enable Link"
          sx={{ mb: 2 }}
        />

        {isLinkEnabled && (
          <TextField
            label="URL"
            fullWidth
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://example.com"
            sx={{ mb: 2 }}
            helperText="Text will be clickable after upload"
          />
        )}

        <Divider sx={{ my: 3 }} />

        {/* WhatsApp Style Background */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Text Background (WhatsApp Style)
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={hasBackground}
              onChange={(e) => setHasBackground(e.target.checked)}
            />
          }
          label="Enable Background"
          sx={{ mb: 2 }}
        />

        {hasBackground && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" gutterBottom display="block">
                Background Color
              </Typography>
              <input
                type="color"
                value={backgroundColor}
                onChange={(e) => setBackgroundColor(e.target.value)}
                style={{
                  width: "100%",
                  height: "45px",
                  border: "2px solid #ddd",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" gutterBottom>
                Background Opacity: {Math.round(backgroundOpacity * 100)}%
              </Typography>
              <Slider
                value={backgroundOpacity}
                onChange={(e, value) => setBackgroundOpacity(value)}
                min={0}
                max={1}
                step={0.1}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
              />
            </Box>
          </>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Category Selection */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          <Category sx={{ mr: 1, verticalAlign: "middle" }} />
          Select Category (Required for Upload)
        </Typography>

        <FormGroup sx={{ mb: 2, pl: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={categories.student}
                onChange={handleCategoryChange}
                name="student"
                color="primary"
              />
            }
            label="Student"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={categories.institute}
                onChange={handleCategoryChange}
                name="institute"
                color="primary"
              />
            }
            label="Institute"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={categories.staff}
                onChange={handleCategoryChange}
                name="staff"
                color="primary"
              />
            }
            label="Staff"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={categories.login}
                onChange={handleCategoryChange}
                name="login"
                color="primary"
              />
            }
            label="Login"
          />
        </FormGroup>

        <Box
          sx={{
            mb: 2,
            p: 1.5,
            bgcolor: "#f5f5f5",
            borderRadius: 1,
            border: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Selected: <strong>{getSelectedCategories()}</strong>
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Action Buttons */}
        <Stack spacing={2}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            startIcon={<Add />}
            onClick={addTextToCanvas}
            disabled={!currentText.trim()}
          >
            Add Text to Canvas
          </Button>

          <Button
            variant="outlined"
            color="error"
            fullWidth
            startIcon={<Delete />}
            onClick={deleteSelected}
            disabled={!selectedObject || selectedObject.isBackgroundImage}
          >
            Delete Selected
          </Button>

          <Button
            variant="contained"
            color="success"
            fullWidth
            startIcon={<Download />}
            onClick={downloadImage}
          >
            Download Ultra HQ Image
          </Button>

          {/* Upload to Server Button */}
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            startIcon={<CloudUpload />}
            onClick={uploadToAPI}
            disabled={uploading}
          >
            {uploading ? "Uploading..." : "Upload to Server"}
          </Button>

          {/* Upload Status Alert */}
          {uploadStatus && (
            <Alert
              severity={
                uploadError ? "error" : uploading ? "info" : "success"
              }
              sx={{ width: "100%" }}
            >
              {uploadStatus}
            </Alert>
          )}
        </Stack>

        <Box sx={{ mt: 3, p: 2, bgcolor: "#e3f2fd", borderRadius: 1 }}>
          <Typography variant="caption" color="primary">
            💡 <strong>Ultra Quality Tips:</strong>
            <br />
            • Canvas: {CANVAS_WIDTH}×{CANVAS_HEIGHT}px display
            <br />
            • Export: {CANVAS_WIDTH * EXPORT_QUALITY_MULTIPLIER}×{CANVAS_HEIGHT * EXPORT_QUALITY_MULTIPLIER}px actual ({EXPORT_QUALITY_MULTIPLIER}x quality)
            <br />
            • Text renders without caching for maximum sharpness
            <br />
            • Click and drag text to move it
            <br />
            • Resize text by dragging corners
            <br />
            • Double-click text to edit directly
            <br />
            • Enable link for clickable text
            <br />
            • Links auto-scale to high-res coordinates
            <br />• Perfect for print and digital displays
          </Typography>
        </Box>
      </Paper>

      {/* Right Panel - Canvas Preview */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
          bgcolor: "#e0e0e0",
        }}
      >
        <Paper
          elevation={8}
          sx={{
            p: 2,
            bgcolor: "white",
          }}
        >
          <canvas ref={canvasRef} />
        </Paper>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 2, textAlign: "center" }}
        >
          Canvas: {CANVAS_WIDTH} × {CANVAS_HEIGHT}px
          <br />
          Export: {CANVAS_WIDTH * EXPORT_QUALITY_MULTIPLIER} × {CANVAS_HEIGHT * EXPORT_QUALITY_MULTIPLIER}px ({EXPORT_QUALITY_MULTIPLIER}x Quality)
        </Typography>
      </Box>
    </Box>
  );
}
